import {
  mortgagePrincipalOutstanding,
  mortgageFormSectionCalculator,
} from '../../utility/mortgageCalculator';
import { getNumberFromString } from '../../utility/formatNumbers';
import getSum from '../../utility/getSum';
import convertUnitMoney from '../../utility/convertUnitMoney';
import { getTableSum } from '../../utility/getTableSum';
import { isValue } from '../../utility/isValue';
import {
  yearlyPercentFromYearlyValue,
  monthlyValueFromYearlyPercent,
} from '../../utility/percentageConverters';
import FormUIOptions from '../../components/formBuilder/formElements/formUIOptions';

export default {
  title: 'Property Summary',
  description: 'Summary of your property details on purchase',
  schema: [
    //the first section is a ghost section that is used to calculate the market purchase price, implemented because this is not calculated in the embedded form. for future refactoring,
    //we should have a dedicated location to enter calculations in the schema that do not appear in the UI
    {
      sectionType: '',
      sectionLabel: '-',
      sectionCondition: {
        field: 'fieldThatDoesNotExist',
        value: 'valueThatDoesNotExist',
      },
      schema: [
        {
          type: FormUIOptions.Number,
          unit: '$',
          label: '',
          name: 'marketPurchasePrice',
          derivedFrom: [
            'localCapRate',
            'totalOperatingExpenses',
            'targetValueSelectPurchase',
            'marketPurchasePriceManual',
            'usePurchaseValuation',
            'purchasePrice',
            'totalGrossRentalIncome',
            'totalOtherIncome',
          ],
          calcFunction: (
            localCapRate,
            totalOperatingExpenses,
            targetValueSelectPurchase,
            marketPurchasePriceManual,
            usePurchaseValuation,
            purchasePrice,
            ...args
          ) => {
            if (!usePurchaseValuation) {
              return purchasePrice;
            }

            if (targetValueSelectPurchase === 2) {
              return marketPurchasePriceManual;
            }

            const netOperatingIncome =
              getSum(...args) * 12 - totalOperatingExpenses * 12;

            const marketPurchasePrice =
              netOperatingIncome / (localCapRate / 100);
            return Math.round(marketPurchasePrice * 100) / 100;
          },
          condition: [
            {
              field: 'usePurchaseValuation',
              isLocal: true,
              value: true,
            },
          ],
        },
        {
          type: FormUIOptions.Number,
          unit: '$',
          label: '',
          name: 'totalOtherIncomeOnPurchase',
          derivedFrom: ['totalOtherIncome'],
          calcFunction: (totalOtherIncome) => totalOtherIncome,
          condition: [
            {
              field: 'usePurchaseValuation',
              isLocal: true,
              value: true,
            },
          ],
        },
      ],
    },
    {
      sectionType: 'banner',
      sharing: false,
      sectionLabel: '',
      layoutClasses: '',
      sectionValue: 'purchasePriceNotification',
      sectionCondition: {
        field: 'usePurchaseValuation',
        value: true,
      },
      positiveMessageArr: [
        'Estimate value on purchase ',
        ' The purchase price is ',
        ' below market value.',
      ],
      negativeMessageArr: [
        'Estimate value on purchase ',
        ' The purchase price is ',
        ' above market value.',
      ],
      sectionDescription:
        'The purchase price is the purchase price you input for the property. The market purchase price is the estimated market value of the property based on the income, expenses, and market cap rate that you entered.',
      schema: [
        {
          type: FormUIOptions.Money,
          label: 'Purchase Price',
          name: 'purchasePrice',
          decimalPlaces: 0,
        },
        {
          type: FormUIOptions.Money,
          label: 'Market Purchase Price',
          name: 'marketPurchasePrice',
          decimalPlaces: 0,
        },
      ],
    },
    {
      sectionType: 'chart',
      sectionLabel: 'Purchase Summary',
      schema: [
        {
          type: FormUIOptions.Money,
          label: 'Asking Price',
          name: 'askingPrice',
        },
        {
          type: FormUIOptions.Money,
          label: 'Purchase Price',
          name: 'purchasePrice',
        },
        {
          type: FormUIOptions.Money,
          label: 'Insurance Premium',
          name: 'cmhcPremium',
          condition: [
            {
              field: 'shouldCalculateCMHC',
              value: true,
            },
            {
              field: 'mortgageInterestOnly',
              value: false,
            },
            {
              field: 'propertyInCanada',
              value: [undefined, null, 1, ''],
            },
          ],
          formula: (cmhcPremium) => ['+', cmhcPremium],
        },
        {
          type: FormUIOptions.Money,
          label: 'Amount Financed',
          name: 'totalMortgageAmount',
          derivedFrom: ['totalMortgageAmount', 'cashPurchase'],
          calcFunction: (totalMortgageAmount, cashPurchase) => {
            if (cashPurchase) {
              return 0;
            }
            return totalMortgageAmount;
          },
          formula: (totalMortgageAmount) => ['-', totalMortgageAmount],
        },
        {
          type: FormUIOptions.Money,
          label: 'Down Payment',
          name: 'downPaymentAmount',
          emphasize: true,
          groupStart: true,
          total: true,
          derivedFrom: ['downPaymentAmount', 'purchasePrice', 'cashPurchase'],
          calcFunction: (downPaymentAmount, purchasePrice, cashPurchase) => {
            if (cashPurchase) {
              return purchasePrice;
            }
            return downPaymentAmount;
          },
          formula: (downPaymentAmount) => ['=', downPaymentAmount],
        },
        {
          type: FormUIOptions.Money,
          label: 'Closing Costs',
          name: 'totalCashRequiredToClose',
          dropDown: true,
          formula: (totalCashRequiredToClose) => [
            '+',
            totalCashRequiredToClose,
          ],
        },
        {
          type: FormUIOptions.Table,
          label: '',
          name: 'closingCosts',
          dropDownDependency: 'totalCashRequiredToClose',
          condition: [
            {
              field: 'cashPurchase',
              value: false,
            },
            {
              field: 'enterTotalClosingCosts',
              value: false,
            },
          ],
          schema: [
            {
              type: FormUIOptions.Text,
              name: 'closingCostName',
              label: 'Closing Cost',
              dropDownDependency: 'totalCashRequiredToClose',
            },
            {
              type: FormUIOptions.Money,
              label: 'Amount',
              layout: 'default',
              name: 'closingCostAmount',
              unit: '$',
              dropDownDependency: 'totalCashRequiredToClose',
            },
          ],
        },
        {
          type: FormUIOptions.Money,
          label: 'Cash Required to Close',
          total: true,
          groupStart: true,
          emphasize: true,
          name: 'totalCashInvestedOnPurchase',
          derivedFrom: ['downPaymentAmount', 'totalCashRequiredToClose'],
          calcFunction: (downPaymentAmount, totalCashRequiredToClose) => {
            return downPaymentAmount + totalCashRequiredToClose;
          },
          formula: (totalCashInvestedOnPurchase) => [
            '=',
            totalCashInvestedOnPurchase,
          ],
        },
        {
          type: FormUIOptions.Money,
          label: ['Underpayment', 'Overpayment'],
          groupStart: true,
          name: 'overUnderPayment',
          tooltipHeading: 'Underpayment/Overpayment',
          description:
            'The difference between the purchase price and the market value of the property, which is based on the local cap rate provided.',
          derivedFrom: ['marketPurchasePrice', 'purchasePrice'],
          calcFunction: (marketPurchasePrice, purchasePrice) => {
            return marketPurchasePrice - purchasePrice;
          },
          condition: {
            field: 'usePurchaseValuation',
            value: true,
          },
        },
        {
          type: FormUIOptions.Money,
          label: 'Down Payment',
          name: 'downPaymentAmount',
          unit: '$',
          formula: (downPaymentAmount) => ['+', downPaymentAmount],
          condition: {
            field: 'usePurchaseValuation',
            value: true,
          },
        },
        {
          type: FormUIOptions.Money,
          label: 'Equity on Purchase',
          name: 'equityOnPurchase',
          tooltipHeading: 'Equity on Purchase',
          description:
            'The amount of the equity that you own, based on the market value and the down payment.',
          groupStart: true,
          total: true,
          emphasize: true,
          derivedFrom: ['downPaymentAmount', 'overUnderPayment'],
          calcFunction: (downPaymentAmount, overUnderPayment) => {
            return downPaymentAmount + overUnderPayment;
          },
          formula: (equityOnPurchase) => ['=', equityOnPurchase],
        },
      ],
      chartSchema: {
        title: 'Purchase Cost',
        type: 'pie',
        schemas: [
          {
            title: 'Purchase Cost',
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Down Payment',
                name: 'downPaymentAmount',
                unit: '$',
              },
              {
                type: FormUIOptions.Money,
                label: 'Closing Cost',
                name: 'totalCashRequiredToClose',
                unit: '$',
              },
            ],
          },
          {
            title: 'Closing Costs',
            schema: [
              {
                type: FormUIOptions.Table,
                label: 'Closing Costs',
                name: 'closingCosts',
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'closingCostName',
                    label: 'Closing Cost',
                  },
                  {
                    type: FormUIOptions.Money,
                    name: 'closingCostAmount',
                    unit: '$',
                  },
                ],
              },
            ],
            condition: {
              field: 'enterTotalClosingCosts',
              value: false,
            },
          },
        ],
      },
    },
    {
      sectionType: 'twoColumn',
      leftSchema: {
        sectionLabel: 'Financing Details',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Loan Amount',
            name: 'totalMortgageAmount',
            dropDown: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Mortgage Amount',
            layout: 'default',
            name: 'mortgageAmount',
            unit: '$',
            dropDownDependency: 'totalMortgageAmount',
            derivedFrom: ['purchasePrice', 'downPayment'],
            calcFunction: (purchasePrice, downPayment) => {
              if (isValue(purchasePrice, downPayment)) {
                const result = purchasePrice - downPayment;

                return result;
              } else {
                return null;
              }
            },
            condition: {
              field: 'cashPurchase',
              value: false,
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Insurance Premium',
            name: 'cmhcPremium',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'shouldCalculateCMHC',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
              {
                field: 'propertyInCanada',
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-Only',
            layout: 'default',
            name: 'mortgageInterestOnly',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'hasSecondMortgage',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'interestRate',
            dropDownDependency: 'totalMortgageAmount',
            unit: '%',
            condition: {
              field: 'cashPurchase',
              value: false,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'interestOnlyMonthly',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'mortgageInterestOnly',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            layout: 'default',
            unit: 'years',
            name: 'interestOnlyLoanPeriod',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'mortgageInterestOnly',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'amortization',
            unit: 'years',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'mortgageInterestOnly',
                value: !true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan To Value',
            layout: 'default',
            name: 'loanToValueFirstMortgage',
            unit: '%',
            dropDownDependency: 'totalMortgageAmount',
            derivedFrom: ['mortgageAmount', 'marketPurchasePrice'],
            calcFunction: (mortgageAmount, purchasePrice) => {
              if (isValue(mortgageAmount, purchasePrice)) {
                const result = (mortgageAmount / purchasePrice) * 100;
                return result;
              } else {
                return null;
              }
            },
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'Monthly Payment',
            layout: 'default',
            name: 'monthlyPayment',
            unit: '$',
            groupEnd: true,
            dropDownDependency: 'totalMortgageAmount',
            derivedFrom: [
              'interestRate',
              'mortgageAmount',
              'amortization',
              'mortgageInterestOnly',
              'interestOnlyMonthly',
              'shouldCalculateCMHC',
              'cmhcPremium',
              'propertyInCanada',
            ],
            calcFunction: (
              interestRate,
              mortgageAmount,
              amortization,
              mortgageInterestOnly,
              interestOnlyMonthly,
              shouldCalculateCMHC,
              cmhcPremium,
              propertyInCanada
            ) => {
              let mortgageTotal = mortgageAmount;
              if (
                shouldCalculateCMHC &&
                cmhcPremium &&
                !mortgageInterestOnly &&
                propertyInCanada !== 0
              ) {
                mortgageTotal += cmhcPremium;
              }

              return mortgageFormSectionCalculator(
                mortgageTotal,
                interestRate,
                mortgageInterestOnly,
                interestOnlyMonthly,
                amortization
              );
            },
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          //type table is for listing items that have been entered in a customizable table / array of objects
          {
            type: FormUIOptions.Table,
            label: 'Additional Financing',
            name: 'purchaseAdditionalFinancing',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
            schema: [
              {
                type: FormUIOptions.Text,
                name: 'loanNameTable',
                label: 'Loan Name',
                dropDownDependency: 'totalMortgageAmount',
              },
              {
                type: FormUIOptions.Money,
                label: 'Loan Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                unit: '$',
                dropDownDependency: 'totalMortgageAmount',
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-Only',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                dropDownDependency: 'totalMortgageAmount',
                unit: '%',
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                unit: 'years',
                name: 'interestOnlyLoanPeriodTable',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                unit: 'years',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: false,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan To Value',
                layout: 'default',
                name: 'loanToValueTable',
                unit: 'years',
                dropDownDependency: 'totalMortgageAmount',
                mainValue: 'percent',
                displayPercentFrom: [
                  'mortgageAmountTable',
                  'marketPurchasePrice',
                ],
                displayPercentFunction: (
                  mortgageAmountTable,
                  purchasePrice
                ) => {
                  return (
                    yearlyPercentFromYearlyValue(mortgageAmountTable, [
                      purchasePrice,
                    ]) || ' -'
                  );
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                groupEnd: true,
                unit: '$',
                dropDownDependency: 'totalMortgageAmount',
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan to Value (LTV)',
            name: 'loanToValue',
            description:
              'The loan amount as a percentage of the property value',
            tooltipHeading: 'Loan to Value (LTV)',
            tooltipFormula: ['LTV', '=', 'Loan Amount', '/', 'Property Value'],
            unit: '%',
            derivedFrom: ['totalMortgageAmount', 'marketPurchasePrice'],
            calcFunction: (totalMortgageAmount, purchasePrice) => {
              if (!isValue(totalMortgageAmount, purchasePrice)) return null;
              return (
                Math.round((totalMortgageAmount / purchasePrice) * 100 * 100) /
                  100 || 0
              );
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Monthly Payment',
            layout: 'default',
            name: 'monthlyDebtService',
            emphasize: true,
            unit: '$',
            derivedFrom: [
              'monthlyPayment',
              'purchaseAdditionalFinancing',
              'cashPurchase',
            ],
            calcFunction: (
              monthlyPayment,
              purchaseAdditionalFinancing,
              cashPurchase
            ) => {
              if (cashPurchase) {
                return 0;
              }
              let totalMonthlyPayment = monthlyPayment;
              totalMonthlyPayment += getTableSum(
                purchaseAdditionalFinancing,
                'monthlyPaymentTable'
              );

              return totalMonthlyPayment;
            },
          },
        ],
      },
      rightSchema: {
        sectionLabel: 'Cash Flow',
        sectionSubLabel: 'Monthly',
        annualValue: true,
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Gross Rent',
            name: 'totalRentalIncomeOnPurchase',
            tooltipHeading: 'Gross Rent',
            description: 'Rental income across all units',
            unit: '$',
            derivedFrom: ['rentalIncomePreVacancy'],
            calcFunction: (rentalIncomePreVacancy) => {
              return rentalIncomePreVacancy;
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Vacancy',
            layout: 'default',
            name: 'vacancy',
            unit: '$',
            derivedFrom: ['vacancyDeduction'],
            calcFunction: (vacancyDeduction) => {
              const vacancy = convertUnitMoney(vacancyDeduction);
              if (isValue(vacancy)) {
                return Math.round(vacancy * 100) / 100;
              } else {
                return 0;
              }
            },
            formula: (vacancy) => ['-', `${vacancy}`],
            displayPercentFrom: [
              'vacancyDeduction',
              'totalRentalIncomeOnPurchase',
            ],
            displayPercentFunction: (vacancyDeduction, totalRentalIncome) => {
              if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                return Math.round((vacancyDeduction / totalRentalIncome) * 100);
              } else {
                return 0;
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Additional Income',
            name: 'totalAdditionalIncome',
            dropDown: true,
            tooltipHeading: 'Additional Income',
            description: 'Additional income except for rent across all units',
            unit: '$',
            derivedFrom: ['totalOtherIncome'],
            calcFunction: (totalOtherIncome) => totalOtherIncome,
            formula: (totalAdditionalIncome) => [
              '+',
              `${totalAdditionalIncome}`,
            ],
          },
          {
            type: FormUIOptions.Table,
            label: '',
            name: 'otherPropertyIncome',
            dropDownDependency: 'totalAdditionalIncome',
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
              {
                field: 'enterTotalOtherIncome',
                value: false,
              },
            ],
            schema: [
              {
                type: FormUIOptions.Text,
                name: 'otherIncomeName',
                label: 'Other Income',
                dropDownDependency: 'totalAdditionalIncome',
              },
              {
                type: FormUIOptions.UnitMoney,
                label: 'Amount',
                layout: 'default',
                name: 'otherIncomeAmount',
                unit: '$',
                dropDownDependency: 'totalAdditionalIncome',
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'Gross Income (GOI)',
            name: 'monthlyGOI',
            emphasize: true,
            tooltipHeading: 'Gross Income (GOI)',
            description:
              'Rental income across all units adjusted for vacancy rate',
            unit: '$',
            derivedFrom: ['totalGrossRentalIncome', 'totalOtherIncome'],
            calcFunction: (...arg) => {
              return getSum(...arg);
            },
            formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
            total: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Total Operating Expenses',
            layout: 'default',
            name: 'totalMonthlyOperatingExpensesPurchase',
            dropDown: true,
            unit: '$',
            derivedFrom: ['totalOperatingExpenses'],
            calcFunction: (totalOperatingExpenses) => totalOperatingExpenses,
            formula: (totalMonthlyOperatingExpensesPurchase) => [
              '-',
              `${totalMonthlyOperatingExpensesPurchase}`,
            ],
          },
          {
            type: FormUIOptions.Table,
            label: '',
            name: 'operatingExpenses',
            dropDownDependency: 'totalMonthlyOperatingExpensesPurchase',
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
              {
                field: 'enterTotalExpenses',
                value: false,
              },
            ],
            schema: [
              {
                type: FormUIOptions.Text,
                name: 'expenseName',
                label: 'Expense',
                dropDownDependency: 'totalMonthlyOperatingExpensesPurchase',
              },
              {
                type: FormUIOptions.UnitMoney,
                derivedFrom: 'rentalIncomePreVacancy',
                label: 'Amount',
                layout: 'default',
                name: 'expenseAmount',
                unit: '$',
                dropDownDependency: 'totalMonthlyOperatingExpensesPurchase',
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'Net Income (NOI)',
            layout: 'default',
            name: 'monthlyNOI',
            emphasize: true,
            tooltipHeading: 'Net Income (NOI)',
            description:
              'Income from all units adjusted for total operating expenses',
            unit: '$',
            derivedFrom: [
              'monthlyGOI',
              'totalMonthlyOperatingExpensesPurchase',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpensesPurchase
            ) => {
              if (isValue(monthlyGOI, totalMonthlyOperatingExpensesPurchase)) {
                return (
                  Math.round(
                    monthlyGOI * 100 -
                      totalMonthlyOperatingExpensesPurchase * 100
                  ) / 100
                );
              } else {
                return null;
              }
            },
            formula: (monthlyNOI) => ['=', `${monthlyNOI}`],
            total: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Monthly Payment',
            name: 'monthlyDebtService',
            unit: '$',
            formula: (monthlyDebtService) => ['-', `${monthlyDebtService}`],
          },
          {
            type: FormUIOptions.Money,
            label: 'Cash Flow',
            layout: 'default',
            isAnnual: true,
            name: 'annualPreTaxCashFlow',
            emphasize: true,
            dynamic: true,
            unit: '$',
            derivedFrom: [
              'monthlyGOI',
              'totalMonthlyOperatingExpensesPurchase',
              'totalMonthlyMortgagePayment',
              'cashPurchase',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpensesPurchase,
              totalMonthlyMortgagePayment,
              cashPurchase
            ) => {
              if (
                isValue(
                  monthlyGOI,
                  totalMonthlyOperatingExpensesPurchase,
                  totalMonthlyMortgagePayment
                )
              ) {
                const monthlyPreTaxIncome =
                  monthlyGOI - totalMonthlyOperatingExpensesPurchase;

                if (cashPurchase) {
                  return monthlyPreTaxIncome * 12;
                }

                const monthlyPreTaxCashFlow =
                  monthlyPreTaxIncome - totalMonthlyMortgagePayment;

                const result = monthlyPreTaxCashFlow * 12;
                return result;
              } else {
                return null;
              }
            },
            formula: (annualPreTaxCashFlow) => ['=', `${annualPreTaxCashFlow}`],
          },
        ],
      },
    },
    {
      sectionType: 'twoColumn',
      layoutClasses: 'order-first',
      leftSchema: {
        sectionLabel: 'Investment Metrics',
        percentageValue: true,
        annualValue: true,
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Cash on Cash',
            tooltipHeading: 'Cash on Cash',
            dynamic: true,
            description: 'Annual pre-tax cash flow expressed as a percentage ',
            tooltipFormula: [
              'Cash on Cash',
              '=',
              'Annual Pre-Tax Cash Flow',
              '/',
              'Total Cash Invested',
            ],
            layout: 'default',
            isAnnual: true,
            mainValue: 'percent',
            showMonthly: false,
            showAnnual: false,
            name: 'annualPreTaxCashFlow',
            unit: '$',
            displayPercentFrom: [
              'annualPreTaxCashFlow',
              'downPaymentAmount',
              'totalCashRequiredToClose',
              'cashPurchase',
              'purchasePrice',
            ],
            displayPercentFunction: (
              annualPreTaxCashFlow,
              downPaymentAmount,
              totalCashRequiredToClose,
              cashPurchase,
              purchasePrice
            ) => {
              const downPayment = cashPurchase
                ? purchasePrice
                : downPaymentAmount;

              return (
                yearlyPercentFromYearlyValue(annualPreTaxCashFlow, [
                  downPayment,
                  totalCashRequiredToClose,
                ]) || ' -'
              );
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Principal Recapture',
            layout: 'default',
            name: 'yearOnePrincipalReduction',
            tooltipHeading: 'Principal Recapture',
            mainValue: 'percent',
            description:
              'Shows how much of the borrowed amount is paid back in the first 12 months, as a percentage of the total cash invested.',
            unit: '$',
            isAnnual: true,
            derivedFrom: [
              'interestRate',
              'mortgageAmount',
              'monthlyPayment',
              'cashPurchase',
              'purchaseAdditionalFinancing',
            ],
            calcFunction: (
              interestRate,
              mortgageAmount,
              monthlyPayment,
              cashPurchase,
              purchaseAdditionalFinancing
            ) => {
              if (cashPurchase) {
                return null;
              }

              if (
                isValue(interestRate, mortgageAmount, monthlyPayment || null)
              ) {
                const firstMortgageOutstandingAmount =
                  mortgagePrincipalOutstanding(
                    interestRate,
                    getNumberFromString(mortgageAmount),
                    getNumberFromString(monthlyPayment),
                    12
                  );

                const firstMortgageReduction =
                  Math.round(
                    getNumberFromString(mortgageAmount) * 100 -
                      firstMortgageOutstandingAmount * 100
                  ) / 100;

                let additionalMortgageReduction = 0;

                if (Array.isArray(purchaseAdditionalFinancing)) {
                  purchaseAdditionalFinancing.forEach((loan) => {
                    const {
                      mortgageAmountTable,
                      monthlyPaymentTable,
                      interestRateTable,
                    } = loan;
                    if (
                      !isValue(
                        mortgageAmountTable,
                        monthlyPaymentTable || null,
                        interestRateTable
                      )
                    ) {
                      return;
                    }

                    const outstandingAmount = mortgagePrincipalOutstanding(
                      interestRateTable,
                      getNumberFromString(mortgageAmountTable),
                      getNumberFromString(monthlyPaymentTable),
                      12
                    );

                    additionalMortgageReduction +=
                      Math.round(
                        getNumberFromString(mortgageAmountTable) * 100 -
                          outstandingAmount * 100
                      ) / 100;
                  });
                }

                return firstMortgageReduction + additionalMortgageReduction;
              } else {
                return null;
              }
            },
            displayPercentFrom: [
              'yearOnePrincipalReduction',
              'downPaymentAmount',
              'totalCashRequiredToClose',
            ],
            displayPercentFunction: (
              yearOnePrincipalReduction,
              downPaymentAmount,
              totalCashRequiredToClose
            ) => {
              return (
                yearlyPercentFromYearlyValue(yearOnePrincipalReduction, [
                  downPaymentAmount,
                  totalCashRequiredToClose,
                ]) || ' -'
              );
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Passive Appreciation',
            layout: 'default',
            name: 'marketAppreciationAmountPurchase',
            tooltipHeading: 'Passive Appreciation',
            description:
              'Annual passive appreciation, based on the passive appreciation estimate.',
            tooltipFormula: [
              'Passive Appreciation',
              '=',
              'Property Value',
              'x',
              'Passive Appreciation Percentage',
              '/',
              'Total Cash Invested',
            ],
            unit: '$',
            mainValue: 'percent',
            isAnnual: true,
            derivedFrom: ['marketAppreciationPercent', 'marketPurchasePrice'],
            calcFunction: (marketAppreciationPercent, purchasePrice) => {
              if (isValue(marketAppreciationPercent, purchasePrice)) {
                const result =
                  (purchasePrice * marketAppreciationPercent) / 100;
                return result;
              } else {
                return null;
              }
            },
            displayPercentFrom: [
              'marketAppreciationAmountPurchase',
              'downPaymentAmount',
              'totalCashRequiredToClose',
            ],
            displayPercentFunction: (
              marketAppreciationAmount,
              downPaymentAmount,
              closingCost
            ) => {
              return (
                yearlyPercentFromYearlyValue(marketAppreciationAmount, [
                  downPaymentAmount,
                  closingCost,
                ]) || ' -'
              );
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Total ROI Year 1',
            isAnnual: true,
            groupEnd: true,
            emphasize: true,
            layout: 'default',
            mainValue: 'percent',
            name: 'totalRoiYear1',
            description: 'Total return on investment in the first year',
            tooltipHeading: 'Total ROI Year 1',
            tooltipFormula: [
              'Total ROI Year 1',
              '=',
              'Cash on Cash',
              '+',
              'Principal Recapture',
              '+',
              'Passive Appreciation',
            ],
            unit: '%',
            derivedFrom: [
              'yearOnePrincipalReduction',
              'annualPreTaxCashFlow',
              'marketAppreciationAmountPurchase',
              'downPaymentAmount',
              'totalCashRequiredToClose',
            ],
            calcFunction: (...args) => {
              const [
                yearOnePrincipalReduction,
                annualPreTaxCashFlow,
                marketAppreciationAmount,
                ...rest
              ] = [...args];
              const totalCashRequiredToClose = getSum(...rest);

              const ROI =
                getSum(
                  yearOnePrincipalReduction +
                    annualPreTaxCashFlow +
                    marketAppreciationAmount
                ) / totalCashRequiredToClose;

              const roiPercentage = Math.round(ROI * 1000) / 10;

              return roiPercentage;
            },
            displayMonthlyFrom: [
              'totalRoiYear1',
              'downPaymentAmount',
              'totalCashRequiredToClose',
            ],
            displayMonthlyFunction: (
              totalRoiYear1,
              downPaymentAmount,
              totalCashRequiredToClose
            ) => {
              const res = monthlyValueFromYearlyPercent(totalRoiYear1, [
                downPaymentAmount,
                totalCashRequiredToClose,
              ]);
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Cap Rate (Purchase)',
            description: 'Cap rate based on the purchase price',
            tooltipHeading: 'Cap Rate (Purchase)',
            tooltipFormula: [
              'Cap Rate (Purchase)',
              '=',
              'Annual Net Operating Income',
              '/',
              'Purchase Price',
            ],
            layout: 'default',
            name: 'capRateInitial',
            mainValue: 'percent',
            showMonthly: false,
            showAnnual: false,
            unit: '%',
            isAnnual: true,
            derivedFrom: [
              'monthlyGOI',
              'totalMonthlyOperatingExpensesPurchase',
              'purchasePrice',
            ],
            calcFunction: (
              monthlyGOI,
              totalMonthlyOperatingExpensesPurchase,
              purchasePrice
            ) => {
              const netMonthlyIncome =
                monthlyGOI - totalMonthlyOperatingExpensesPurchase;

              return (
                Math.round(
                  (getNumberFromString(netMonthlyIncome) * 12 * 10000) /
                    purchasePrice
                ) / 100
              );
            },
            displayMonthlyFrom: ['capRateInitial', 'purchasePrice'],
            displayMonthlyFunction: (capRate, purchasePrice) => {
              return monthlyValueFromYearlyPercent(capRate, [purchasePrice]);
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Monthly NOI',
            tooltipHeading: 'Net Operating Income',
            groupEnd: true,
            showAnnualBrackets: true,
            description:
              'Net Operating Income. The total income from the property after deducting all operating expenses, but before deducting mortgage payments and taxes.',
            layout: 'default',
            name: 'monthlyNOI',
            per: 'month',
            unit: '$',
          },
        ],
      },
      rightSchema: {
        sectionLabel: 'Financial Ratios',
        sectionSubLabel: 'Hover for Details',
        schema: [
          {
            type: FormUIOptions.Number,
            label: 'Rent to Value',
            name: 'rentToValue',
            unit: '%',
            layout: 'default',
            tooltipHeading: 'Rent to Value',
            description: 'Annual rent as a percentage of the property value.',
            tooltipFormula: [
              'Rent to Value',
              '=',
              'Annual Rent',
              '/',
              'Purchase Price',
            ],
            derivedFrom: ['monthlyGOI', 'purchasePrice'],
            calcFunction: (monthlyGOI, purchasePrice) => {
              if (isValue(monthlyGOI, purchasePrice)) {
                return (
                  Math.round(
                    (getNumberFromString(monthlyGOI) * 12 * 10000) /
                      purchasePrice
                  ) / 100
                );
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Gross Rent Multiplier',
            layout: 'default',
            name: 'grossRentMultiplier',
            tooltipHeading: 'Gross Rent Multiplier',
            description:
              'The ratio between the property value and the gross rental income.',
            tooltipFormula: [
              'Gross Rent Multiplier',
              '=',
              'Purchase Price',
              '/',
              'Annual Rent',
            ],
            unit: '',
            derivedFrom: ['monthlyGOI', 'purchasePrice'],
            calcFunction: (monthlyGOI, purchasePrice) => {
              if (isValue(monthlyGOI, purchasePrice)) {
                const monthlyGOIValue = getNumberFromString(monthlyGOI);
                const purchasePriceValue = getNumberFromString(purchasePrice);

                const yearlyGOI = monthlyGOIValue * 12;
                const result = purchasePriceValue / yearlyGOI;
                return Math.round(result * 100) / 100;
              } else {
                return null;
              }
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Equity Multiple',
            layout: 'default',
            name: 'equityMultiple',
            tooltipHeading: 'Equity Multiple',
            description:
              'The ratio between the total equity and the total cash invested.',
            tooltipFormula: [
              'Equity Multiple',
              '=',
              '(Total Equity',
              '+',
              'Cumulative Cash Flow)',
              '/',
              'Total Cash Invested',
            ],
            unit: '',
            derivedFrom: [
              'cashPurchase',
              'marketPurchasePrice',
              'equityOnPurchase',
              'downPaymentAmount',
              'purchasePrice',
              'yearOnePrincipalReduction',
              'annualPreTaxCashFlow',
              'closingCost',
            ],
            calcFunction: (
              cashPurchase,
              marketPurchasePrice,
              equityOnPurchase,
              downPaymentAmount,
              purchasePrice,
              yearOnePrincipalReduction,
              annualPreTaxCashFlow,
              closingCost
            ) => {
              const equity = cashPurchase
                ? marketPurchasePrice
                : equityOnPurchase;

              const downPayment = cashPurchase
                ? purchasePrice
                : downPaymentAmount;

              const totalCashRequiredToClose = getSum(downPayment, closingCost);

              const dividend =
                equity + yearOnePrincipalReduction + annualPreTaxCashFlow;

              return dividend / totalCashRequiredToClose;
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Break Even Ratio',
            name: 'breakEvenRatio',
            tooltipHeading: 'Break Even Ratio',
            description:
              'The % of units that need to be occupied to break even.',
            tooltipFormula: [
              'Break Even Ratio',
              '=',
              '(Yearly Expenses',
              '+',
              'Yearly Loan Payment)',
              '/',
              'Yearly Gross Rent',
            ],
            unit: '%',
            layout: 'default',
            derivedFrom: [
              'monthlyGOI',
              'monthlyDebtService',
              'totalOperatingExpenses',
              'cashPurchase',
            ],
            calcFunction: (
              monthlyGOI,
              monthlyDebtService,
              totalOperatingExpenses,
              cashPurchase
            ) => {
              let yearlyLoanPayment = 0;

              if (!cashPurchase && !isValue(monthlyDebtService)) {
                return null;
              }

              if (!cashPurchase) {
                yearlyLoanPayment = monthlyDebtService * 12;
              }

              const totalExpenses = totalOperatingExpenses * 12;
              const totalIncome = monthlyGOI * 12;

              return ((totalExpenses + yearlyLoanPayment) / totalIncome) * 100;
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Debt Coverage Ratio',
            name: 'debtCoverageRatio',
            tooltipHeading: 'Debt Coverage Ratio',
            description:
              'The ratio between the net operating income and the monthly debt service.',
            tooltipFormula: [
              'Debt Coverage Ratio',
              '=',
              'Net Operating Income',
              '/',
              'Debt Service',
            ],
            layout: 'default',
            dynamic: true,
            red: 1,
            green: 1.1,
            derivedFrom: ['monthlyNOI', 'monthlyDebtService', 'cashPurchase'],
            calcFunction: (monthlyNOI, monthlyDebtService, cashPurchase) => {
              if (cashPurchase) {
                return null;
              }

              if (!isValue(monthlyNOI, monthlyDebtService)) {
                return null;
              }

              return monthlyNOI / monthlyDebtService;
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Debt Yield',
            name: 'debtYield',
            tooltipHeading: 'Debt Yield',
            description:
              'The ratio between the net operating income and the total loan amount.',
            tooltipFormula: [
              'Debt Yield',
              '=',
              'Yearly Net Operating Income',
              '/',
              'Total Loan Amount',
            ],
            layout: 'default',
            unit: '%',
            derivedFrom: ['monthlyNOI', 'totalMortgageAmount', 'cashPurchase'],
            calcFunction: (monthlyNOI, totalMortgageAmount, cashPurchase) => {
              if (cashPurchase) {
                return null;
              }

              if (!isValue(monthlyNOI, totalMortgageAmount)) {
                return null;
              }

              return ((monthlyNOI * 12) / totalMortgageAmount) * 100;
            },
          },
        ],
      },
    },
  ],
};
